
import React, { useState, useEffect, useCallback, useRef } from "react";
import "./MyEarnings.css";
import {
    Wallet,
    Calendar,
    IndianRupee,
    ArrowLeft,
    Download,
    Filter,
    TrendingUp,
    Clock,
    Loader,
    AlertCircle,
    ChevronDown
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { toast } from "react-toastify";

const API_BASE_URL = "https://api.rismicmart.com";



const MyEarnings = () => {
    const navigate = useNavigate();
    const { user, logout } = useAuth();

    // ==================== STATE DECLARATIONS ====================

    const [totalEarnings, setTotalEarnings] = useState(0);
    const [todayEarnings, setTodayEarnings] = useState(0);
    const [thisMonthEarnings, setThisMonthEarnings] = useState(0);
    const [thisWeekEarnings, setThisWeekEarnings] = useState(0);

    const [transactions, setTransactions] = useState([]);
    const [filteredTransactions, setFilteredTransactions] = useState([]);

    const [loading, setLoading] = useState(true);
    const [filterLoading, setFilterLoading] = useState(false);

    const [selectedFilter, setSelectedFilter] = useState("all"); // all, today, week, month
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    const [sortBy, setSortBy] = useState("date-desc"); // date-desc, date-asc, amount-desc, amount-asc
    const [earningsChart, setEarningsChart] = useState([]);

    const [showFilterDropdown, setShowFilterDropdown] = useState(false);
    const [showSortDropdown, setShowSortDropdown] = useState(false);


    const filterRef = useRef(null);
const sortRef = useRef(null);


    // ==================== AXIOS INSTANCE ====================

    const axiosInstance = axios.create({
        baseURL: API_BASE_URL,
        headers: {
            "Content-Type": "application/json",
        }
    });

    axiosInstance.interceptors.request.use((config) => {
        const token = localStorage.getItem("deliveryToken");
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    });

useEffect(() => {
  function handleClickOutside(event) {
    // if click is outside filter dropdown, close it
    if (filterRef.current && !filterRef.current.contains(event.target)) {
      setShowFilterDropdown(false);
    }

    // if click is outside sort dropdown, close it
    if (sortRef.current && !sortRef.current.contains(event.target)) {
      setShowSortDropdown(false);
    }
  }

  function handleKeyDown(event) {
    if (event.key === "Escape") {
      setShowFilterDropdown(false);
      setShowSortDropdown(false);
    }
  }

  document.addEventListener("mousedown", handleClickOutside);
  document.addEventListener("keydown", handleKeyDown);

  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
    document.removeEventListener("keydown", handleKeyDown);
  };
}, []);
    
    // ==================== HELPER FUNCTIONS ====================

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric"
        });
    };

    const formatTimeOnly = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleTimeString("en-IN", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true
        });
    };

    const getToday = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    };

    const getThisWeekStart = () => {
        const today = new Date();
        const day = today.getDay();
        const diff = today.getDate() - day;
        const date = new Date(today.setDate(diff));
        date.setHours(0, 0, 0, 0);
        return date;
    };

    const getThisMonthStart = () => {
        const date = new Date();
        date.setDate(1);
        date.setHours(0, 0, 0, 0);
        return date;
    };

    const calculateEarnings = useCallback((orders) => {
        if (!orders || orders.length === 0) {
            return {
                total: 0,
                today: 0,
                thisWeek: 0,
                thisMonth: 0
            };
        }

        const delivered = orders.filter(o => o.status === "delivered");
        const today = getToday();
        const thisWeekStart = getThisWeekStart();
        const thisMonthStart = getThisMonthStart();

        let total = 0;
        let todayEarn = 0;
        let weekEarn = 0;
        let monthEarn = 0;

        delivered.forEach(order => {
            const amount = parseFloat(order.amount) || 0;
            total += amount;

            const orderDate = new Date(order.deliveredAt || order.updatedAt);
            orderDate.setHours(0, 0, 0, 0);

            if (orderDate >= today) {
                todayEarn += amount;
            }

            if (orderDate >= thisWeekStart) {
                weekEarn += amount;
            }

            if (orderDate >= thisMonthStart) {
                monthEarn += amount;
            }
        });

        return {
            total,
            today: todayEarn,
            thisWeek: weekEarn,
            thisMonth: monthEarn
        };
    }, []);

    const formatTransactionForDisplay = (order) => {
        return {
            _id: order._id,
            orderId: `#ORD-${order._id?.slice(-6).toUpperCase()}`,
            customerName: order.name || "Customer",
            amount: parseFloat(order.amount) || 0,
            date: order.deliveredAt || order.updatedAt,
            status: order.status,
            paymentMethod: order.paymentMethod || "Online",
            address: order.address || "Delivery Address",
            itemsCount: order.products?.length || 0,
            rating: order.deliveryRating || null
        };
    };

    const sortTransactions = useCallback((txns, sortMethod) => {
        const sorted = [...txns];

        switch (sortMethod) {
            case "date-desc":
                return sorted.sort((a, b) => new Date(b.date) - new Date(a.date));
            case "date-asc":
                return sorted.sort((a, b) => new Date(a.date) - new Date(b.date));
            case "amount-desc":
                return sorted.sort((a, b) => b.amount - a.amount);
            case "amount-asc":
                return sorted.sort((a, b) => a.amount - b.amount);
            default:
                return sorted;
        }
    }, []);

    const filterTransactions = useCallback((txns, filterType) => {
        if (filterType === "all") {
            return txns;
        }

        const today = getToday();
        const thisWeekStart = getThisWeekStart();
        const thisMonthStart = getThisMonthStart();

        return txns.filter(txn => {
            const txnDate = new Date(txn.date);
            txnDate.setHours(0, 0, 0, 0);

            switch (filterType) {
                case "today":
                    return txnDate >= today;
                case "week":
                    return txnDate >= thisWeekStart;
                case "month":
                    return txnDate >= thisMonthStart;
                default:
                    return true;
            }
        });
    }, []);

    


    // ==================== FETCH EARNINGS ====================

    const fetchEarnings = useCallback(async () => {
        if (!user?._id) {
            console.log("❌ No user ID found");
            return;
        }

        try {
            setLoading(true);
            console.log("📊 Fetching earnings for delivery person:", user._id);

            const response = await axiosInstance.get(
                `/api/orders/delivery-person/${user._id}`
            );

            if (response.data.success) {
                const orders = response.data.data || [];
                console.log("📦 Orders fetched:", orders.length);

                // Calculate earnings
                const earnings = calculateEarnings(orders);

                console.log("💰 Earnings calculated:", earnings);

                setTotalEarnings(earnings.total);
                setTodayEarnings(earnings.today);
                setThisWeekEarnings(earnings.thisWeek);
                setThisMonthEarnings(earnings.thisMonth);

                // Format transactions
                const formattedTxns = orders
                    .filter(o => o.status === "delivered")
                    .map(formatTransactionForDisplay);

                console.log("📋 Transactions formatted:", formattedTxns.length);

                setTransactions(formattedTxns);

                // Apply initial filtering and sorting
                const filteredTxns = filterTransactions(formattedTxns, "all");
                const sortedTxns = sortTransactions(filteredTxns, "date-desc");
                setFilteredTransactions(sortedTxns);

                // Generate earnings chart data (daily earnings for this month)
                generateEarningsChart(orders);
            }
        } catch (error) {
            console.error("❌ Error fetching earnings:", error);
            if (error.response?.status === 401) {
                logout();
                navigate("/login");
            } else {
                toast.error("Failed to load earnings data", {
                    position: "bottom-right",
                    autoClose: 3000
                });
            }
        } finally {
            setLoading(false);
        }
    }, [user?._id, calculateEarnings, filterTransactions, sortTransactions, logout, navigate]);

    const generateEarningsChart = (orders) => {
        const thisMonthStart = getThisMonthStart();
        const daysInMonth = new Date(
            thisMonthStart.getFullYear(),
            thisMonthStart.getMonth() + 1,
            0
        ).getDate();

        const dailyEarnings = {};

        // Initialize all days with 0
        for (let i = 1; i <= daysInMonth; i++) {
            dailyEarnings[i] = 0;
        }

        // Calculate earnings for each day
        orders
            .filter(o => o.status === "delivered")
            .forEach(order => {
                const orderDate = new Date(order.deliveredAt || order.updatedAt);

                if (orderDate >= thisMonthStart) {
                    const day = orderDate.getDate();
                    const amount = parseFloat(order.amount) || 0;
                    dailyEarnings[day] = (dailyEarnings[day] || 0) + amount;
                }
            });

        const chartData = Object.entries(dailyEarnings).map(([day, amount]) => ({
            day: parseInt(day),
            amount: amount,
            label: `Day ${day}`
        }));

        setEarningsChart(chartData);
    };

    // ==================== APPLY FILTER ====================

    const applyFilter = useCallback((filterType) => {
        setFilterLoading(true);
        setSelectedFilter(filterType);

        setTimeout(() => {
            const filtered = filterTransactions(transactions, filterType);
            const sorted = sortTransactions(filtered, sortBy);
            setFilteredTransactions(sorted);
            setFilterLoading(false);
            setShowFilterDropdown(false);
        }, 300);
    }, [transactions, filterTransactions, sortTransactions, sortBy]);

    // ==================== APPLY SORT ====================

    const applySort = useCallback((sortMethod) => {
        setFilterLoading(true);
        setSortBy(sortMethod);

        setTimeout(() => {
            const sorted = sortTransactions(filteredTransactions, sortMethod);
            setFilteredTransactions(sorted);
            setFilterLoading(false);
            setShowSortDropdown(false);
        }, 300);
    }, [filteredTransactions, sortTransactions]);

    // ==================== DOWNLOAD REPORT ====================

    const downloadReport = () => {
        try {
            let csvContent = "data:text/csv;charset=utf-8,";
            
            // Add header
            csvContent += "Earnings Report - " + new Date().toLocaleDateString("en-IN") + "\n";
            csvContent += "Delivery Partner: " + user?.fullName + "\n";
            csvContent += "Contact: " + user?.phone + "\n";
            csvContent += "\n";
            
            // Add summary
            csvContent += "SUMMARY\n";
            csvContent += "Total Earnings," + formatCurrency(totalEarnings) + "\n";
            csvContent += "Today Earnings," + formatCurrency(todayEarnings) + "\n";
            csvContent += "This Week Earnings," + formatCurrency(thisWeekEarnings) + "\n";
            csvContent += "This Month Earnings," + formatCurrency(thisMonthEarnings) + "\n";
            csvContent += "\n";
            
            // Add transactions
            csvContent += "TRANSACTIONS\n";
            csvContent += "Order ID,Customer Name,Amount,Date,Time,Payment Method,Items\n";
            
            filteredTransactions.forEach(txn => {
                csvContent += `"${txn.orderId}","${txn.customerName}","${txn.amount}","${formatDate(txn.date)}","${formatTimeOnly(txn.date)}","${txn.paymentMethod}","${txn.itemsCount}"\n`;
            });

            const encodedUri = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", `earnings_report_${new Date().getTime()}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            toast.success("Report downloaded successfully!", {
                position: "bottom-right",
                autoClose: 3000
            });
        } catch (error) {
            console.error("Error downloading report:", error);
            toast.error("Failed to download report", {
                position: "bottom-right",
                autoClose: 3000
            });
        }
    };

    // ==================== USEEFFECTS ====================

    useEffect(() => {
        if (user?._id) {
            fetchEarnings();
        } else {
            navigate("/login");
        }
    }, [user?._id, fetchEarnings, navigate]);

    // ==================== RENDER ====================


    
    if (!user) {
        return (
            <div className="earning-container" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
                <div style={{ textAlign: "center" }}>
                    <Loader className="spinner" size={48} />
                    <h2>Loading Earnings...</h2>
                </div>
            </div>
        );
    }



    
    return (
        <div className="earning-container">
            {/* ==================== BACK BUTTON ==================== */}
            <button
                className="back-btn"
                onClick={() => navigate("/dashboard")}
                title="Go back to dashboard"
            >
                <ArrowLeft size={20} />
                <span>Back</span>
            </button>

            {/* ==================== HEADER ==================== */}
            <div className="earning-header">
                <div>
                    <h2>My Earnings</h2>
                    <p className="header-subtitle">Track your daily, weekly, and monthly earnings</p>
                </div>
                <button
                    className="download-btn"
                    onClick={downloadReport}
                    disabled={loading}
                    title="Download earnings report as CSV"
                >
                    <Download size={18} />
                    <span>Download Report</span>
                </button>
            </div>

            {/* ==================== LOADING STATE ==================== */}
            {loading ? (
                <div style={{ textAlign: "center", padding: "60px 20px" }}>
                    <Loader className="spinner" size={40} />
                    <p style={{ marginTop: "20px", color: "#666" }}>Loading earnings data...</p>
                </div>
            ) : (
                <>
                
                    {/* ==================== EARNINGS SUMMARY CARDS ==================== */}
                    <div className="earning-summary">
                        <div className="earning-card total">
                            <div className="icon">
                                <Wallet size={26} />
                            </div>
                            <div className="details">
                                <h3>Total Earnings</h3>
                                <p className="amount">{formatCurrency(totalEarnings)}</p>
                                <span className="subtitle">{transactions.length} deliveries</span>
                            </div>
                        </div>

                        <div className="earning-card today">
                            <div className="icon">
                                <Calendar size={26} />
                            </div>
                            <div className="details">
                                <h3>Today's Earnings</h3>
                                <p className="amount">{formatCurrency(todayEarnings)}</p>
                                <span className="subtitle">{new Date().toLocaleDateString("en-IN")}</span>
                            </div>
                        </div>

                        <div className="earning-card week">
                            <div className="icon">
                                <TrendingUp size={26} />
                            </div>
                            <div className="details">
                                <h3>This Week</h3>
                                <p className="amount">{formatCurrency(thisWeekEarnings)}</p>
                                <span className="subtitle">Last 7 days</span>
                            </div>
                        </div>

                        <div className="earning-card month">
                            <div className="icon">
                                <Clock size={26} />
                            </div>
                            <div className="details">
                                <h3>This Month</h3>
                                <p className="amount">{formatCurrency(thisMonthEarnings)}</p>
                                <span className="subtitle">{new Date().toLocaleDateString("en-IN", { month: "long" })}</span>
                            </div>
                        </div>
                    </div>


                    {/* ==================== FILTER & SORT CONTROLS ==================== */}
                    <div className="transactions-controls">
                        <h4>Recent Transactions</h4>
                        <div className="controls-wrapper">
                            <div className="filter-sort-buttons">
                                {/* Filter Dropdown */}
                                <div className="dropdown-container" ref={filterRef}>
                                    <button
                                        className={`filter-btn ${selectedFilter !== "all" ? "active" : ""}`}
                                        onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                                        title="Filter transactions"
                                    >
                                        <Filter size={16} />
                                        <span>
                                            {selectedFilter === "all" ? "All" : selectedFilter.charAt(0).toUpperCase() + selectedFilter.slice(1)}
                                        </span>
                                        <ChevronDown size={16} className="dropdown-arrow" />
                                    </button>

                                    {showFilterDropdown && (
                                        <div className="dropdown-menu">
                                            <button
                                                className={`dropdown-item ${selectedFilter === "all" ? "active" : ""}`}
                                                onClick={() => applyFilter("all")}
                                            >
                                                All Transactions
                                            </button>
                                            <button
                                                className={`dropdown-item ${selectedFilter === "today" ? "active" : ""}`}
                                                onClick={() => applyFilter("today")}
                                            >
                                                Today
                                            </button>
                                            <button
                                                className={`dropdown-item ${selectedFilter === "week" ? "active" : ""}`}
                                                onClick={() => applyFilter("week")}
                                            >
                                                This Week
                                            </button>
                                            <button
                                                className={`dropdown-item ${selectedFilter === "month" ? "active" : ""}`}
                                                onClick={() => applyFilter("month")}
                                            >
                                                This Month
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Sort Dropdown */}
                                <div className="dropdown-container" ref={sortRef}>
                                    <button
                                        className={`sort-btn ${sortBy !== "date-desc" ? "active" : ""}`}
                                        onClick={() => setShowSortDropdown(!showSortDropdown)}
                                        title="Sort transactions"
                                    >
                                        <span>
                                            Sort: {
                                                sortBy === "date-desc" ? "Newest" :
                                                sortBy === "date-asc" ? "Oldest" :
                                                sortBy === "amount-desc" ? "Highest Amount" :
                                                "Lowest Amount"
                                            }
                                        </span>
                                        <ChevronDown size={16} className="dropdown-arrow" />
                                    </button>

                                    {showSortDropdown && (
                                        <div className="dropdown-menu">
                                            <button
                                                className={`dropdown-item ${sortBy === "date-desc" ? "active" : ""}`}
                                                onClick={() => applySort("date-desc")}
                                            >
                                                Newest First
                                            </button>
                                            <button
                                                className={`dropdown-item ${sortBy === "date-asc" ? "active" : ""}`}
                                                onClick={() => applySort("date-asc")}
                                            >
                                                Oldest First
                                            </button>
                                            <button
                                                className={`dropdown-item ${sortBy === "amount-desc" ? "active" : ""}`}
                                                onClick={() => applySort("amount-desc")}
                                            >
                                                Highest Amount
                                            </button>
                                            <button
                                                className={`dropdown-item ${sortBy === "amount-asc" ? "active" : ""}`}
                                                onClick={() => applySort("amount-asc")}
                                            >
                                                Lowest Amount
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Transaction Count */}
                            <span className="transaction-count">
                                {filteredTransactions.length} transaction{filteredTransactions.length !== 1 ? "s" : ""}
                            </span>
                        </div>
                    </div>


                    {/* ==================== TRANSACTIONS LIST ==================== */}
                    <div className="recent-transactions">
                        {filterLoading ? (
                            <div style={{ textAlign: "center", padding: "40px" }}>
                                <Loader className="spinner" size={32} />
                                <p style={{ marginTop: "10px", color: "#666" }}>Applying filters...</p>
                            </div>
                        ) : filteredTransactions.length === 0 ? (
                            <div className="empty-state">
                                <AlertCircle size={48} color="#ccc" />
                                <h4>No Transactions Found</h4>
                                <p>
                                    {selectedFilter === "all"
                                        ? "You haven't completed any deliveries yet"
                                        : `No transactions found for this period`}
                                </p>
                            </div>
                        ) : (
                            <div className="transaction-list">
                                {filteredTransactions.map((transaction, index) => (
                                    <div key={transaction._id} className="transaction-item">
                                        <div className="transaction-index">
                                            <span>{index + 1}</span>
                                        </div>

                                        <div className="t-left">
                                            <p className="order-id">{transaction.orderId}</p>
                                            <span className="customer-name">{transaction.customerName}</span>
                                            <span className="transaction-date">
                                                {formatDate(transaction.date)} at {formatTimeOnly(transaction.date)}
                                            </span>
                                            <span className="payment-method">{transaction.paymentMethod}</span>
                                        </div>

                                        <div className="t-middle">
                                            <span className="items-count">📦 {transaction.itemsCount} item{transaction.itemsCount !== 1 ? "s" : ""}</span>
                                            {transaction.rating && (
                                                <span className="transaction-rating">
                                                    ⭐ {transaction.rating.toFixed(1)}/5
                                                </span>
                                            )}
                                        </div>

                                        <div className="t-right">
                                            <div className="amount-container">
                                                <IndianRupee size={16} />
                                                <span className="amount">{formatCurrency(transaction.amount)}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* ==================== PAGINATION INFO ==================== */}
                    {filteredTransactions.length > 0 && (
                        <div className="pagination-info">
                            <p>
                                Showing {filteredTransactions.length} of {transactions.length} total transaction{transactions.length !== 1 ? "s" : ""}
                            </p>
                            {selectedFilter !== "all" && (
                                <button
                                    className="clear-filter-btn"
                                    onClick={() => applyFilter("all")}
                                >
                                    Clear Filters
                                </button>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default MyEarnings;
